import { Injectable } from '@angular/core';

import { UrlConstants, API_ENDPOINTS } from '@varsity/commons';
import { HttpHelperService, TokenResponse, LoggingService, LocalStorageService, NotificationsService, User } from '@varsity/services';
import { Observable, Subscription, ReplaySubject } from 'rxjs';
import { SiteLayout } from '../events/events.model';
import { StorageData } from './token.model';

/*
  Generated class for the TokenService provider.
  See https://angular.io/guide/dependency-injection for more info on providers
  and Angular DI.
*/
@Injectable()
export class TokenService {
    tokenExpireDelta: number = 600; // seconds
    private tokenIntervalSubscription: Subscription;
    private timeUntilTokenExpiresChanged = new ReplaySubject<number>(1);
    /*
     * Observable that streams the time until the token expires
     */
    timeUntilTokenExpires$ = this.timeUntilTokenExpiresChanged.asObservable();

    private generateTokenPromise: Observable<TokenResponse>;
    private extendTokenPromise: Observable<string>;
    private ensureValidTokenPromise: Promise<TokenResponse>;

    constructor(
        private httpHelperService: HttpHelperService,
        private loggingService: LoggingService,
        private notificationsService: NotificationsService,
        private localStorageService: LocalStorageService) {

        this.loggingService.log('Hello TokenSercvice Provider');
    }



    /*
     * Generate a new token
     */
    generateToken(): Observable<TokenResponse> {
        this.loggingService.info('generating token');

        // we need to make sure we generate only one token,
        // even if we are calling this multiple times at the same time

        if (this.generateTokenPromise) {
            return this.generateTokenPromise;
        }

        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.genericTokenAPI;

        let promise = this.httpHelperService
            .get<TokenResponse>(url,
                {
                    params: {
                        "Site": (window as any).__GlobalSettings.Product,
                        "GenericFlag": "true"
                    }
                }
            );

        this.generateTokenPromise = promise;

        let subscription = promise.finally(() => {
            subscription.unsubscribe();
        }).subscribe(
            data => {
                if (data.SuccessFlag && data.Token) {
                    //Check for Embedded, don't remove SponsorID on relaod
                    var strEmbed = this.localStorageService.get("EmbedFlag");
                    if (strEmbed == "") { strEmbed = "false"; }
                    // when we generate a new token, we clear everything in the storage
                    if (Boolean(this.localStorageService.get("EmbedFlag")) == false) {
                        this.localStorageService.clear();
                        this.localStorageService.add("token", data.Token);
                        this.localStorageService.add("defaultSiteLayout", JSON.stringify(data.trSiteLayout));
                        this.localStorageService.add("enableLogRocket", JSON.stringify(data.EnableLogging));
                        this.localStorageService.add("loggingProjId", data.LoggingProjID);
                    } else {
                        this.localStorageService.remove("token");
                        this.localStorageService.add("token", data.Token);
                        this.localStorageService.add("defaultSiteLayout", JSON.stringify(data.trSiteLayout));
                    }

                    // notify that the user is logged out
                    this.notificationsService.notifyUserLoginChanged(null);
                    this.notificationsService.notifyConfirmationChanged(null);

                    let subscription = this.getTokenExpire().finally(() => {
                        this.loggingService.info(`generated token ${this.localStorageService.get('token')} valid until ${this.localStorageService.get('tokenExpireDate')}`);
                        this.generateTokenPromise = null;
                        subscription.unsubscribe();
                    }).subscribe(data => {
                        let date = new Date();
                        let expireDate = new Date(date.getTime() + (data * 1000));
                        this.localStorageService.add("tokenExpireDate", expireDate.toString());
                    });
                } else {
                    this.loggingService.error(`Did not generate new token`, data);
                    this.generateTokenPromise = null;
                }
            },
            error => {
                this.loggingService.error("Token Failure: ", error);
                this.generateTokenPromise = null;
            }
        );

        return promise;
    }

    /*
     * Get the time left until the current token expires
     */
    getTokenExpire(): Observable<number> {
        this.loggingService.debug('get token expire');

        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.tokenCheckExpiredUrl;

        let promise = this.httpHelperService
            .get<number>(url,
                {
                    params: {
                        "Token": this.getToken()
                    }
                });

        return promise;
    }

    /*
     * Get the time left until the current token expires
     */
    extendToken(): Observable<string> {
        this.loggingService.info('extending token');

        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.tokenExtend;

        if (this.extendTokenPromise) {
            return this.extendTokenPromise;
        }

        let promise = this.httpHelperService
            .get<string>(url,
                {
                    params: {
                        "TokenValue": this.getToken(),
                        "ExtendFlag": "1"
                    }
                });

        this.extendTokenPromise = promise;

        let subscription = promise.finally(() => {
            this.extendTokenPromise = null;
            subscription.unsubscribe();
        }).subscribe(
            data => {
                this.loggingService.debug("Token Extend Success: ", data);
            },
            error => {
                this.loggingService.error("Token Extend Failure: ", error);
            }
        );

        return promise;
    }

    /*
     * Make sure that the token is not expired
     */
    ensureValidToken(forceExtend: boolean = false): Promise<TokenResponse> {
        this.loggingService.debug('ensure token valid');

        const currDate = new Date();

        if (this.ensureValidTokenPromise) {
            return this.ensureValidTokenPromise;
        }

        // need to split the promise from the execute of it, in order to store the promise
        // in the ensureValidTokenPromise
        let runLogic: () => void;

        var promise = new Promise<TokenResponse>((resolve, reject) => {
            runLogic = () => {
                if (!this.getTokenExpireDate() || this.getTokenExpireDate() <= currDate) {
                    // token is expired
                    var subscription = this.generateToken().subscribe((value) => {
                        subscription.unsubscribe();
                        this.ensureValidTokenPromise = null;
                        resolve(value);
                    }, () => {
                        this.ensureValidTokenPromise = null;
                        reject();
                    });
                } else {
                    var data = this.getTokenExpireDate().getTime() - currDate.getTime();
                    if (data != 0) {
                        data = data / 1000;
                    }

                    // forceExtend to make sure the token is valid as much as possible
                    if (data >= this.tokenExpireDelta && !forceExtend) {
                        // we know the token does not expire in the next 30 seconds
                        var response = new TokenResponse();
                        response.Token = this.getToken();
                        response.trSiteLayout = JSON.parse(this.localStorageService.get("defaultSiteLayout"))
                        response.SuccessFlag = true;
                        this.ensureValidTokenPromise = null;
                        resolve(response);
                    } else {
                        // TODO
                        // show notification that session is about to expire
                        // or automatically extend it
                        let subscription = this.extendToken().finally(() => {
                            subscription.unsubscribe();
                        }).subscribe((newToken) => {
                            if (newToken) {
                                // set the new token
                                this.localStorageService.add("token", newToken);
                                let subscription = this.getTokenExpire().finally(() => {
                                    subscription.unsubscribe();
                                }).subscribe(data => {
                                    if (data) {
                                        let date = new Date();
                                        let expireDate = new Date(date.getTime() + (data * 1000));
                                        this.localStorageService.add("tokenExpireDate", expireDate.toString());

                                        var response = new TokenResponse();
                                        response.Token = this.getToken();
                                        response.trSiteLayout = JSON.parse(this.localStorageService.get("defaultSiteLayout"))
                                        response.SuccessFlag = true;

                                        this.loggingService.info(`got new token: ${response.Token}. expire: ${expireDate}`);

                                        this.ensureValidTokenPromise = null;
                                        resolve(response);
                                    } else {
                                        this.loggingService.error('token timeout not returned after we extended it');
                                        // token is expired
                                        var subscription = this.generateToken().subscribe((value) => {
                                            subscription.unsubscribe();
                                            this.ensureValidTokenPromise = null;
                                            resolve(value);
                                        }, () => {
                                            this.ensureValidTokenPromise = null;
                                            reject();
                                        });
                                    }
                                }, () => {
                                    this.ensureValidTokenPromise = null;
                                    reject();
                                });
                            } else {
                                this.loggingService.error('extend token did not return a new one');
                                // token is expired
                                var subscription = this.generateToken().subscribe((value) => {
                                    subscription.unsubscribe();
                                    this.ensureValidTokenPromise = null;
                                    resolve(value);
                                }, () => {
                                    this.ensureValidTokenPromise = null;
                                    reject();
                                });
                            }
                        }, () => {
                            this.ensureValidTokenPromise = null;
                            reject();
                        });
                    }
                }
            }
        });

        this.ensureValidTokenPromise = promise;

        runLogic();

        return promise;
    }

    initializeTokenTimer() {
        // if we already have one, unsubscribe
        if (this.tokenIntervalSubscription) {
            this.tokenIntervalSubscription.unsubscribe();
        }

        // create an interval
        var tokenInterval = Observable.interval(1000);

        // subscribe to the interval
        this.tokenIntervalSubscription = tokenInterval.subscribe((val) => {
            const currDate = new Date();
            difference = Number.MAX_VALUE;

            if (this.getTokenExpireDate()) {
                var difference = this.getTokenExpireDate().getTime() - currDate.getTime();
                if (difference != 0) {
                    difference /= 1000;
                }
            }

            // stream the new value
            this.timeUntilTokenExpiresChanged.next(difference);
        });
    }

    getToken(): string {
        let token = this.localStorageService.get("token");
        return token;
    }

    getTokenExpireDate(): Date | null {
        let date = this.localStorageService.get("tokenExpireDate");
        if (date) {
            return new Date(date);
        }

        return null;
    }

    saveCartToken(stripeToken: string, cartFlag: number = 1, storageData: StorageData): Observable<number> {
        this.loggingService.debug('Saving cart token...');

        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.saveCartToken;

        let promise = this.httpHelperService
            .post<number>(url,
                {
                    User: storageData.User,
                    DefaultSiteLayout: storageData.DefaultSiteLayout,
                    Token: this.getToken(),
                    TokenExpireDate: storageData.TokenExpireDate,
                    Confirmation: storageData.Confirmation
                },
                {
                    params: {
                        "Token": this.getToken(),
                        "StripeToken": stripeToken,
                        "CartFlag": cartFlag
                    }
                });

        return promise;

    }

    getCartToken(token: string, stripeToken: string): Observable<any> {
        this.loggingService.debug('Retrieving cart after payment');
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.getCartToken;

        let promise = this.httpHelperService
            .get<any>(url,
                {
                    params: {
                        // TODO we should not send the Token, just the StripeToken
                        //"Token": this.getToken(),
                        "Token": token,
                        "StripeToken": stripeToken
                    }
                });

        return promise;

    }
}





// WEBPACK FOOTER //
// ./src/services/token/token.service.ts